import type { ClockSlot, ScheduleRule, SchedulerConfiguration } from './types';

const allWeekdays = [1, 2, 3, 4, 5, 6, 7];

export function formatClockOffset(offsetSeconds: number): string {
  const minutes = Math.floor(offsetSeconds / 60);
  const seconds = offsetSeconds % 60;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function slotMinute(slot: ClockSlot): number {
  return Math.floor(slot.offsetSeconds / 60);
}

export function separationExplanation(kind: 'track' | 'artist', minutes: number): string {
  const normalized = Math.max(0, Number.isFinite(minutes) ? minutes : 0);
  const value = Number.isInteger(normalized) ? String(normalized) : String(Math.round(normalized * 100) / 100);
  if (normalized === 0) return '0 означає: це обмеження вимкнено.';

  return kind === 'track'
    ? `${value} означає: не ставити цей самий трек раніше ніж через ${value} хв.`
    : `${value} означає: після треку виконавця не ставити жоден його трек протягом ${value} хв.`;
}

export function normalizeClockSlots(slots: ClockSlot[]): ClockSlot[] {
  return [...slots]
    .sort((left, right) => left.position - right.position || left.offsetSeconds - right.offsetSeconds || left.id.localeCompare(right.id))
    .map((slot, position) => ({ ...slot, position, offsetSeconds: position }));
}

export function isBaseScheduleRule(rule: ScheduleRule): boolean {
  return rule.clockTemplateId !== null
    && rule.programId === null
    && rule.startLocal === rule.endLocal
    && allWeekdays.every((day) => rule.weekdays.includes(day));
}

export function baseMatrixId(configuration: SchedulerConfiguration): string | null {
  const candidates = configuration.scheduleRules
    .filter((rule) => rule.enabled && isBaseScheduleRule(rule))
    .sort((left, right) => left.priority - right.priority || left.id.localeCompare(right.id));

  return candidates[0]?.clockTemplateId ?? null;
}

export function assignBaseMatrix(configuration: SchedulerConfiguration, matrixId: string): SchedulerConfiguration {
  const base = configuration.scheduleRules
    .filter(isBaseScheduleRule)
    .sort((left, right) => left.priority - right.priority || left.id.localeCompare(right.id))[0];
  const scheduleRules = base === undefined
    ? [...configuration.scheduleRules, createBaseRule(matrixId)]
    : configuration.scheduleRules.map((rule) => rule.id === base.id
      ? { ...rule, clockTemplateId: matrixId, programId: null, priority: -1000, enabled: true }
      : rule);

  return { ...configuration, scheduleRules };
}

export function createBaseRule(matrixId: string): ScheduleRule {
  return {
    id: crypto.randomUUID(),
    clockTemplateId: matrixId,
    programId: null,
    validFrom: null,
    validUntil: null,
    weekdays: [...allWeekdays],
    startLocal: '00:00:00',
    endLocal: '00:00:00',
    priority: -1000,
    enabled: true,
  };
}

export function validateConfiguration(configuration: SchedulerConfiguration): string | null {
  if (configuration.rotationCategories.length === 0) return 'Додайте хоча б одну ротацію.';
  if (configuration.clockTemplates.length === 0) return 'Додайте хоча б одну матрицю плейлистів.';
  if (baseMatrixId(configuration) === null) return 'Оберіть базову матрицю плейлистів.';

  for (const clock of configuration.clockTemplates) {
    if (clock.slots.length === 0) return `Матриця «${clock.name}» не має жодної позиції.`;
    const positions = new Set(clock.slots.map((slot) => slot.position));
    if (positions.size !== clock.slots.length) return `Матриця «${clock.name}» має дубльовані позиції.`;
    for (const slot of clock.slots) {
      if (slot.slotType === 'ROTATION' && slot.rotationCategoryId === null) return 'Позиція ротації не має категорії.';
      if (slot.slotType === 'ITEM' && (slot.fixedTrackId === null || !isUuid(slot.fixedTrackId))) return 'Фіксована позиція не має коректного ідентифікатора аудіоматеріалу.';
    }
  }

  return null;
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

export function createStarterConfiguration(stationId: string, stationRevision: number): SchedulerConfiguration {
  const categoryId = crypto.randomUUID();
  const clockId = crypto.randomUUID();
  const slots: ClockSlot[] = Array.from({ length: 12 }, (_, position) => ({
    id: crypto.randomUUID(),
    offsetSeconds: position,
    slotType: 'ROTATION',
    rotationCategoryId: categoryId,
    fixedTrackId: null,
    transitionProfileId: null,
    settings: {},
    position,
  }));

  return {
    stationId,
    stationRevision,
    rotationCategories: [{
      id: categoryId,
      code: 'MUSIC',
      name: 'Основна музика',
      minTrackSeparationSeconds: 10_800,
      minArtistSeparationSeconds: 3_600,
      minCategorySeparationSeconds: 0,
      maxSameGenreSequence: 3,
      defaultWeight: 1,
      priority: 0,
      allowedDays: null,
      allowedHours: null,
      enabled: true,
      criteria: { itemTypes: ['TRACK'], artists: [], genres: [], languages: [], bpmMin: null, bpmMax: null },
    }],
    clockTemplates: [{ id: clockId, name: 'Базова музична матриця', enabled: true, slots }],
    programs: [],
    scheduleRules: [{
      id: crypto.randomUUID(),
      clockTemplateId: clockId,
      programId: null,
      validFrom: null,
      validUntil: null,
      weekdays: [1, 2, 3, 4, 5, 6, 7],
      startLocal: '00:00:00',
      endLocal: '00:00:00',
      priority: -1000,
      enabled: true,
    }],
  };
}
